import { Accordion, GridContainer } from "@fluidattacks/design";
import _ from "lodash";
import * as React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Detail } from "../../detail";
import type { ITreatmentDetailsProps } from "../../types";
import { Value } from "../../value";
import { VulnerabilityTreatment } from "gql/graphql";
import { TreatmentFormatter } from "pages/finding/vulnerabilities/formatters";

const TreatmentDetailsSection: React.FC<ITreatmentDetailsProps> = ({
  vulnerability,
  vulnerabilityAdditionalInfo,
}: ITreatmentDetailsProps): JSX.Element => {
  const { t } = useTranslation();

  const isVulnOpen = vulnerability.state === "VULNERABLE";

  const currentJustification =
    !isVulnOpen ||
    _.isUndefined(vulnerability.treatmentJustification) ||
    _.isNull(vulnerability.treatmentJustification)
      ? ""
      : vulnerability.treatmentJustification;

  const currentExpiration =
    isVulnOpen &&
    vulnerabilityAdditionalInfo?.treatmentStatus ===
      VulnerabilityTreatment.Accepted &&
    !_.isNull(vulnerabilityAdditionalInfo.treatmentAcceptanceDate)
      ? vulnerabilityAdditionalInfo.treatmentAcceptanceDate.split(" ")[0]
      : "";

  const treatmentDate = isVulnOpen
    ? vulnerability.lastTreatmentDate.split(" ")[0]
    : "";

  const treatmentStatus = useMemo((): string => {
    if (vulnerabilityAdditionalInfo === undefined) {
      return "";
    }

    return isVulnOpen
      ? (vulnerabilityAdditionalInfo.treatmentStatus ?? "")
      : "";
  }, [isVulnOpen, vulnerabilityAdditionalInfo]);

  const treatmentAcceptanceStatus = useMemo((): string => {
    if (vulnerabilityAdditionalInfo === undefined) {
      return "";
    }

    return isVulnOpen
      ? (vulnerabilityAdditionalInfo.treatmentAcceptanceStatus ?? "")
      : "";
  }, [isVulnOpen, vulnerabilityAdditionalInfo]);

  return (
    <Accordion
      items={[
        {
          description: (
            <GridContainer
              gap={0.75}
              justify={"space-between"}
              lg={2}
              md={2}
              sm={1}
              xl={2}
            >
              <Detail
                field={
                  treatmentStatus === "" ? (
                    <Value value={treatmentStatus} />
                  ) : (
                    <TreatmentFormatter
                      originalTreatment={treatmentStatus}
                      treatmentAcceptanceStatus={treatmentAcceptanceStatus}
                    />
                  )
                }
                label={t("searchFindings.tabVuln.vulnTable.currentTreatment")}
              />
              <Detail
                field={
                  <Value
                    placeHolder={t("searchFindings.tabVuln.notAssigned")}
                    value={vulnerability.treatmentAssigned}
                  />
                }
                label={t("searchFindings.tabVuln.vulnTable.assigned")}
              />
              <Detail
                field={<Value value={treatmentDate} />}
                label={t("searchFindings.tabVuln.vulnTable.treatmentDate")}
              />
              <Detail
                field={<Value value={currentExpiration} />}
                label={t(
                  "searchFindings.tabVuln.vulnTable.treatmentExpirationDate",
                )}
              />
              <Detail
                field={
                  <Value
                    value={vulnerabilityAdditionalInfo?.treatmentChanges}
                  />
                }
                label={t("searchFindings.tabVuln.vulnTable.treatmentChanges")}
              />
              <Detail
                field={
                  <Value
                    placeHolder={t("searchFindings.tabVuln.notProvided")}
                    value={vulnerability.externalBugTrackingSystem}
                  />
                }
                label={t(
                  "searchFindings.tabVuln.vulnTable.treatmentBugTrackingSystemUrl",
                )}
              />
              <Detail
                field={
                  <Value
                    placeHolder={t("searchFindings.tabVuln.notProvided")}
                    value={currentJustification}
                  />
                }
                label={t(
                  "searchFindings.tabVuln.vulnTable.treatmentJustification",
                )}
              />
            </GridContainer>
          ),
          expanded: true,
          title: t("searchFindings.tabVuln.vulnTable.treatmentDetails"),
        },
      ]}
    />
  );
};

export { TreatmentDetailsSection };
